import React, { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { MapPin, Search, ShieldAlert } from "lucide-react";
import ContentLoader from "../../ContentLoader";
import RecordDetailModal from "../../RecordDetailModal";
import SettingsPanelActions from "./SettingsPanelActions";
import { Alert, Pagination as UiPagination } from "../../ui";
import CatalogFilterChip from "../CatalogFilterChip";
import { getSecurityLogs, type SecurityLog } from "../../../services/securityLogsApi";

/**
 * Keep in sync with Common.SecurityEvents in the API (Reimagine.Application/Utilities/Common.cs).
 * tone = app-status chip colour.
 */
const SECURITY_EVENT_TYPES: Record<string, { label: string; tone: string }> = {
  "Security.LoginFailed": { label: "Login failed", tone: "warning" },
  "Security.PasswordResetRequested": { label: "Reset requested", tone: "info" },
  "Security.PasswordResetBlocked": { label: "Reset blocked (privileged)", tone: "danger" },
  "Security.PasswordResetRejected": { label: "Reset rejected", tone: "danger" },
  "Security.PasswordResetCompleted": { label: "Reset completed", tone: "success" },
  "Security.OtpFailed": { label: "Wrong OTP", tone: "warning" },
  "Security.OtpLocked": { label: "OTP locked", tone: "danger" },
  "Security.UnknownEmail": { label: "Unknown email", tone: "muted" },
};

const EVENT_TYPE_OPTIONS = [
  ...Object.entries(SECURITY_EVENT_TYPES).map(([value, { label }]) => ({ value, label })),
];

const PERIOD_OPTIONS = [
  { value: "0", label: "All time" },
  { value: "1", label: "Last 24 hours" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

const PAGE_SIZE = 25;

const EmptyCell = () => <span className="app-td__empty">—</span>;

const eventMeta = (eventType: string) =>
  SECURITY_EVENT_TYPES[eventType] ?? {
    label: eventType?.replace("Security.", "") || "Unknown",
    tone: "muted",
  };

const EventChip = ({ eventType }: { eventType: string }) => {
  const { label, tone } = eventMeta(eventType);
  return <span className={`app-status app-status--${tone}`}>{label}</span>;
};

/** API stores UTC without a zone marker; force UTC so the browser converts to local time. */
const formatTime = (value: string) => {
  if (!value) return "";
  const iso = /Z|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const userLabel = (log: SecurityLog) => log.userEmail || log.userName || null;

/** Loopback / private-network IPs have no public location, so no lookup link is shown. */
const isPublicIp = (ip: string | null) => {
  if (!ip || ip === "Unknown") return false;
  const v4 = ip.replace(/^::ffff:/i, "");
  return !(
    v4 === "::1" ||
    /^127\./.test(v4) ||
    /^10\./.test(v4) ||
    /^192\.168\./.test(v4) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(v4) ||
    /^169\.254\./.test(v4) ||
    /^f[cd]/i.test(v4) ||
    /^fe80:/i.test(v4)
  );
};

/** External lookups open in a new tab; noreferrer keeps our admin URL out of their logs. */
const ipLocationUrl = (ip: string) => `https://ipinfo.io/${encodeURIComponent(ip.replace(/^::ffff:/i, ""))}`;
const ipReputationUrl = (ip: string) =>
  `https://www.abuseipdb.com/check/${encodeURIComponent(ip.replace(/^::ffff:/i, ""))}`;

const IpCell = ({ ip, showReputation = false }: { ip: string | null; showReputation?: boolean }) => {
  if (!ip) return <EmptyCell />;
  if (!isPublicIp(ip)) {
    return (
      <span className="app-td__desc" title="Local or private network address">
        {ip} (local)
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span className="app-td__desc">{ip}</span>
      <a
        href={ipLocationUrl(ip)}
        target="_blank"
        rel="noopener noreferrer"
        title="Check IP location on ipinfo.io"
        onClick={(e) => e.stopPropagation()}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, whiteSpace: "nowrap" }}
      >
        <MapPin size={14} aria-hidden />
        Check location
      </a>
      {showReputation && (
        <a
          href={ipReputationUrl(ip)}
          target="_blank"
          rel="noopener noreferrer"
          title="Check if this IP is reported for abuse on AbuseIPDB"
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, whiteSpace: "nowrap" }}
        >
          <ShieldAlert size={14} aria-hidden />
          Check reputation
        </a>
      )}
    </span>
  );
};

const SecurityLogsPanel = () => {
  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventType, setEventType] = useState("");
  const [days, setDays] = useState("0");
  const [searchTerm, setSearchTerm] = useState("");
  const [pageInfo, setPageInfo] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [viewLog, setViewLog] = useState<SecurityLog | null>(null);
  const searchMountedRef = useRef(false);

  const fetchLogs = useCallback(
    async (page = 1, filters = { eventType, days, search: searchTerm }) => {
      setLoading(true);
      setError(null);
      try {
        const result = await getSecurityLogs({
          pageNumber: page,
          pageSize: PAGE_SIZE,
          eventType: filters.eventType,
          days: Number(filters.days),
          search: filters.search,
        });
        setLogs(result.items);
        setPageInfo({
          currentPage: result.pageNumber,
          totalPages: result.totalPages,
          totalCount: result.totalCount,
        });
      } catch (err: any) {
        console.error("Error fetching security logs:", err);
        setError(err?.message || "Failed to load security logs. Please try again.");
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [eventType, days, searchTerm]
  );

  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search, matching the other settings panels.
  useEffect(() => {
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return undefined;
    }
    const timerId = window.setTimeout(() => fetchLogs(1), 300);
    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleEventTypeChange = (value: string) => {
    setEventType(value);
    fetchLogs(1, { eventType: value, days, search: searchTerm });
  };

  const handleDaysChange = (value: string) => {
    setDays(value);
    fetchLogs(1, { eventType, days: value, search: searchTerm });
  };

  const goToPage = (page: number) => {
    const next = Math.min(Math.max(page, 1), pageInfo.totalPages);
    if (next !== pageInfo.currentPage) fetchLogs(next);
  };

  return (
    <div className="sa-settings-panel">
      <SettingsPanelActions>
        <div className="dash-search-field dash-table-search">
          <Search className="dash-search-field__icon" aria-hidden />
          <input
            type="text"
            placeholder="Search IP, email, or details…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dash-search-field__input"
          />
        </div>
        <CatalogFilterChip
          label="Event"
          options={EVENT_TYPE_OPTIONS}
          value={eventType}
          onChange={handleEventTypeChange}
          disabled={loading}
        />
        <CatalogFilterChip
          label="Period"
          options={PERIOD_OPTIONS}
          value={days}
          onChange={handleDaysChange}
          disabled={loading}
          showAll={false}
        />
      </SettingsPanelActions>

      {loading ? (
        <ContentLoader label="Loading security logs…" />
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : (
        <Fragment>
          <p className="app-td__desc" style={{ margin: "0 0 8px" }}>
            {pageInfo.totalCount} event{pageInfo.totalCount === 1 ? "" : "s"}
          </p>
          <div className="app-table-wrap app-table-wrap--refined">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>User</th>
                  <th>IP address</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center">
                      <span className="app-td__desc">No security events match these filters.</span>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="rdm-row-clickable" onClick={() => setViewLog(log)}>
                      <td>
                        <span className="app-td__title" style={{ whiteSpace: "nowrap" }}>
                          {formatTime(log.eventTime)}
                        </span>
                      </td>
                      <td>
                        <EventChip eventType={log.eventType} />
                      </td>
                      <td>{userLabel(log) ? <span className="app-td__desc">{userLabel(log)}</span> : <EmptyCell />}</td>
                      <td>
                        <IpCell ip={log.ipAddress} />
                      </td>
                      <td>
                        {log.detail ? (
                          <span className="app-td__desc" title={log.detail}>
                            {log.detail}
                          </span>
                        ) : (
                          <EmptyCell />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <UiPagination
            page={pageInfo.currentPage}
            totalPages={pageInfo.totalPages}
            onPageChange={goToPage}
          />
        </Fragment>
      )}

      <RecordDetailModal
        show={!!viewLog}
        onHide={() => setViewLog(null)}
        title={viewLog ? eventMeta(viewLog.eventType).label : "Security event"}
        subtitle="Security event"
        fields={[
          { label: "Time", value: viewLog ? formatTime(viewLog.eventTime) : null },
          { label: "Event type", value: viewLog?.eventType },
          { label: "User", value: viewLog ? userLabel(viewLog) : null },
          { label: "User ID", value: viewLog?.userId ?? null },
          {
            label: "IP address",
            value: viewLog?.ipAddress ? <IpCell ip={viewLog.ipAddress} showReputation /> : null,
            fullWidth: true,
          },
          { label: "Details", value: viewLog?.detail, fullWidth: true },
          { label: "Browser / user agent", value: viewLog?.userAgent, fullWidth: true },
        ]}
      />
    </div>
  );
};

export default SecurityLogsPanel;
